// pages/InvoiceRequest/index.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { invoiceRequestService } from '../../services/invoiceRequest';
import { memberService } from '../../services/member';
import { InvoiceRequest } from '../../types/invoiceRequest';
import { Member } from '../../types/member';
import { buildMemberShortName } from '../../utils/memberDisplay';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { InvoiceRequestEditDialog } from '../../components/InvoiceRequest/InvoiceRequestEditDialog';
import { InvoiceRequestDeleteDialog } from '../../components/InvoiceRequest/InvoiceRequestDeleteDialog';
import { UnrequestedCompletedWorksList } from '../../components/InvoiceRequest/UnrequestedCompletedWorksList';
import { useUser } from '../../hooks/useUser';
import { customerService } from '../../services/customer';
import { Customer } from '../../types/customer';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Link } from 'react-router-dom';

// 検索パラメータの型定義
interface InvoiceRequestSearchParams {
  billingMonth?: string;
  memberName?: string;
  isCompleted?: string;
  customerName?: string;
  quotationNo?: string;
}

// ローカルストレージのキー
const STORAGE_KEY = 'invoiceRequestSearchParams';

const InvoiceRequestListPage: React.FC = () => {
  const { userInfo } = useUser();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [invoiceRequests, setInvoiceRequests] = useState<InvoiceRequest[]>([]);
  const [allInvoiceRequests, setAllInvoiceRequests] = useState<InvoiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 検索結果保持用の状態
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchParams, setSearchParams] = useState<InvoiceRequestSearchParams | null>(null);
  const [lastSavedParams, setLastSavedParams] = useState<InvoiceRequestSearchParams | null>(null);

  // 検索フィルター
  const [billingMonth, setBillingMonth] = useState<string>('');
  const [memberName, setMemberName] = useState('');
  const [isCompleted, setIsCompleted] = useState<string>('all');
  const [customerName, setCustomerName] = useState('');
  const [quotationNo, setQuotationNo] = useState('');

  // 担当者選択関連
  const [members, setMembers] = useState<Member[]>([]);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // 客先名選択関連
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // 申し送り事項表示用
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState('');

  // ステータス更新確認ダイアログ用
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{ id: number, newStatus: boolean } | null>(null);

  // 編集・削除ダイアログ用
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoiceRequest, setSelectedInvoiceRequest] = useState<InvoiceRequest | null>(null);

  // モバイル表示用の展開状態管理
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // 初期化処理（ページ復帰時の検索条件復元）
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        
        // ローカルストレージから検索条件を読み込み
        const savedParams = localStorage.getItem(STORAGE_KEY);
        let searchActive = false;
        let params = null;

        if (savedParams) {
          try {
            const parsedParams = JSON.parse(savedParams) as InvoiceRequestSearchParams;
            if (Object.keys(parsedParams).length > 0) {
              console.log('保存された検索条件を使用:', parsedParams);
              params = parsedParams;
              searchActive = true;
              
              // 検索条件をUIに反映
              setBillingMonth(parsedParams.billingMonth || '');
              setMemberName(parsedParams.memberName || '');
              setIsCompleted(parsedParams.isCompleted || 'all');
              setCustomerName(parsedParams.customerName || '');
              setQuotationNo(parsedParams.quotationNo || '');
            }
          } catch (e) {
            console.error('検索条件の解析エラー:', e);
          }
        }

        // 基本データを読み込み
        await Promise.all([fetchMembers(), fetchCustomers()]);

        // 検索条件に基づいてデータを読み込み
        if (searchActive && params) {
          setSearchParams(params);
          setIsSearchActive(true);
          await executeSearch(params);
        } else {
          setSearchParams(null);
          setIsSearchActive(false);
          await fetchInvoiceRequests();
        }
      } catch (error) {
        console.error('初期化エラー:', error);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [location.key]); // location.keyで他ページからの復帰を検知

  // 検索条件が変更されたときの処理
  useEffect(() => {
    if (!loading) {
      handleSearchConditionChange();
    }
  }, [billingMonth, memberName, isCompleted, selectedCustomer, quotationNo]);

  // 検索条件変更時の処理
  const handleSearchConditionChange = async () => {
    const currentParams: InvoiceRequestSearchParams = {};
    
    if (billingMonth) currentParams.billingMonth = billingMonth;
    if (memberName) currentParams.memberName = memberName;
    if (isCompleted !== 'all') currentParams.isCompleted = isCompleted;
    if (selectedCustomer) currentParams.customerName = selectedCustomer.Customer_Name;
    if (quotationNo) currentParams.quotationNo = quotationNo;

    const hasSearchParams = Object.keys(currentParams).length > 0;

    // 検索条件をローカルストレージに保存または削除
    if (hasSearchParams) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentParams));
      setLastSavedParams(currentParams);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setLastSavedParams(null);
    }

    // 状態を更新
    setSearchParams(currentParams);
    setIsSearchActive(hasSearchParams);

    // 検索を実行
    if (hasSearchParams) {
      await executeSearch(currentParams);
    } else {
      await fetchInvoiceRequests();
    }
  };

  // 検索実行関数
  const executeSearch = async (params: InvoiceRequestSearchParams) => {
    try {
      setLoading(true);
      setError('');

      // サーバーサイド検索用のパラメータ
      const serverParams: any = {};
      if (params.billingMonth) serverParams.billingMonth = params.billingMonth;
      if (params.memberName) serverParams.memberName = params.memberName;
      if (params.isCompleted && params.isCompleted !== 'all') {
        serverParams.isCompleted = params.isCompleted === 'true';
      }

      // APIから請求書データを取得
      const response = await invoiceRequestService.getAll(serverParams);
      setAllInvoiceRequests(response.invoiceRequests);

      // フロントエンド側でフィルタリング
      applyFrontendFilters(response.invoiceRequests, params);
    } catch (err) {
      console.error('請求書作成依頼の取得に失敗しました:', err);
      setError('請求書作成依頼の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceRequests = async () => {
    try {
      setLoading(true);
      setError('');

      // 検索条件なしで全データを取得
      const response = await invoiceRequestService.getAll({});
      setAllInvoiceRequests(response.invoiceRequests);
      setInvoiceRequests(response.invoiceRequests);
    } catch (err) {
      console.error('請求書作成依頼の取得に失敗しました:', err);
      setError('請求書作成依頼の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // フロントエンド側でのフィルタリング関数
  const applyFrontendFilters = (data: InvoiceRequest[], params?: InvoiceRequestSearchParams) => {
    let filteredData = [...data];
    const filterParams = params || searchParams;

    if (!filterParams) {
      setInvoiceRequests(filteredData);
      return;
    }

    // 客先名でのフィルタリング
    if (filterParams.customerName) {
      filteredData = filteredData.filter(item =>
        item.Customer_Name === filterParams.customerName
      );
    }

    // 工事番号でのフィルタリング
    if (filterParams.quotationNo && typeof filterParams.quotationNo === 'string') {
      filteredData = filteredData.filter(item =>
        item.Quotation_No && item.Quotation_No.includes(filterParams.quotationNo!)
      );
    }

    setInvoiceRequests(filteredData);
  };

  // 担当者情報を取得
  const fetchMembers = async () => {
    try {
      const response = await memberService.getAllMembers();
      setMembers(response);
      setFilteredMembers(response);
      return response;
    } catch (err) {
      console.error('担当者情報の取得に失敗しました:', err);
      return [];
    }
  };

  // 顧客情報を取得するための関数を追加
  const fetchCustomers = async () => {
    try {
      const response = await customerService.getAllCustomers();
      setCustomers(response);
      return response;
    } catch (err) {
      console.error('顧客情報の取得に失敗しました:', err);
      return [];
    }
  };

  // 担当者検索フィルタリング
  useEffect(() => {
    if (memberSearchQuery.trim() === '') {
      setFilteredMembers(members);
    } else {
      const query = memberSearchQuery.toLowerCase();
      const filtered = members.filter(member => {
        const fullName = `${member.LastName}${member.FirstName}${member.staff_initial || ''}`.toLowerCase();
        return fullName.includes(query);
      });
      setFilteredMembers(filtered);
    }
  }, [memberSearchQuery, members]);

  const handleOpenMemberDialog = () => {
    setMemberDialogOpen(true);
    setMemberSearchQuery('');
    setFilteredMembers(members);
  };

  const handleCloseMemberDialog = () => {
    setMemberDialogOpen(false);
  };

  const handleSelectMember = (name: string) => {
    setMemberName(name);
    setMemberDialogOpen(false);
  };

  // Autocompleteの変更ハンドラを修正
  const handleCustomerChange = (event: any, newValue: Customer | null) => {
    console.log('顧客選択変更:', newValue);
    setSelectedCustomer(newValue);
  };

  const handleShowRemarks = (remarks: string) => {
    setSelectedRemarks(remarks);
    setRemarksDialogOpen(true);
  };

  const handleUpdateStatusConfirm = (id: number, newStatus: boolean) => {
    setSelectedRequest({ id, newStatus });
    setStatusDialogOpen(true);
  };

  // 検索条件クリア関数を修正
  const clearSearchConditions = async () => {
    console.log('検索条件をクリア');
    
    // ローカルストレージから検索条件を削除
    localStorage.removeItem(STORAGE_KEY);
    setLastSavedParams(null);
    
    // すべての検索条件をクリア
    setBillingMonth('');
    setMemberName('');
    setIsCompleted('all');
    setSelectedCustomer(null);
    setQuotationNo('');
    
    // 状態をリセット
    setIsSearchActive(false);
    setSearchParams(null);
    
    // 全データを再読み込み
    await fetchInvoiceRequests();
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest) return;

    try {
      // ログインユーザーの苗字を取得
      const updatedByName = userInfo?.lastName || "更新者";

      await invoiceRequestService.updateStatus(selectedRequest.id, selectedRequest.newStatus, updatedByName);
      
      // 検索条件を保持して更新
      if (isSearchActive && searchParams) {
        await executeSearch(searchParams);
      } else {
        await fetchInvoiceRequests();
      }
      
      setStatusDialogOpen(false);
    } catch (err) {
      console.error('請求書作成状態の更新に失敗しました:', err);
      setError('請求書作成状態の更新に失敗しました');
    }
  };

  // 編集ダイアログを開く
  const handleOpenEditDialog = (request: InvoiceRequest) => {
    setSelectedInvoiceRequest(request);
    setEditDialogOpen(true);
  };

  // 削除ダイアログを開く
  const handleOpenDeleteDialog = (request: InvoiceRequest) => {
    setSelectedInvoiceRequest(request);
    setDeleteDialogOpen(true);
  };

  // 編集・削除の成功時
  const handleEditSuccess = () => {
    // 検索条件を保持して更新
    if (isSearchActive && searchParams) {
      executeSearch(searchParams);
    } else {
      fetchInvoiceRequests();
    }
    setEditDialogOpen(false);
  };

  const handleDeleteSuccess = () => {
    // 検索条件を保持して更新
    if (isSearchActive && searchParams) {
      executeSearch(searchParams);
    } else {
      fetchInvoiceRequests();
    }
    setDeleteDialogOpen(false);
  };

  // ナビゲーション関数（検索条件の事前保存）
  const handleNavigateToCalculationSheet = (constructionId: number) => {
    // ナビゲーション前に検索条件が確実に保存されているか確認
    if (isSearchActive && searchParams) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
      setLastSavedParams(searchParams);
    }
    navigate(`/calculation-sheet/${constructionId}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}年${(date.getMonth() + 1).toString().padStart(2, '0')}月`;
  };

  const formatFullDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}年${(date.getMonth() + 1).toString().padStart(2, '0')}月${date.getDate().toString().padStart(2, '0')}日`;
  };

  // 行の展開・折りたたみを切り替え
  const handleToggleRow = (index: number) => {
    setExpandedRows(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // モバイル表示
  const renderMobileView = () => (
    <Box>
      {invoiceRequests.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="textSecondary">請求書作成依頼が見つかりません</Typography>
        </Paper>
      ) : (
        invoiceRequests.map((request, index) => (
          <Paper
            key={request.ID}
            elevation={2}
            sx={{
              mb: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '4px solid',
              borderColor: request.Is_Completed ? 'success.main' : 'warning.main',
            }}
          >
            <Box
              onClick={() => handleToggleRow(index)}
              sx={{ cursor: 'pointer' }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Chip
                  label={request.Is_Completed ? '作成済み' : '未作成'}
                  color={request.Is_Completed ? 'success' : 'warning'}
                  size="small"
                  sx={{ height: '24px', fontSize: '0.75rem' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                    }}
                  >
                    {formatDate(request.Billing_Month)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                {request.Quotation_No && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    工事番号: {request.Quotation_No}
                  </Typography>
                )}

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                >
                  {request.Customer_Name}
                </Typography>

                {/* 現場名を追加 */}
                {request.Site_Name && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.9rem',
                      color: 'text.primary',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                    {request.Site_Name}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {request.Construction_Details || '(工事内容なし)'}
                </Typography>

                {/* 金額情報を追加 */}
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>見積金額:</span>
                    <span style={{ fontWeight: 500 }}>
                      {request.Estimated_Amount ?
                        `¥${request.Estimated_Amount.toLocaleString()}` :
                        '-'}
                    </span>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span>請求金額:</span>
                    <span style={{ fontWeight: 500 }}>
                      {request.Invoice_Amount ?
                        `¥${request.Invoice_Amount.toLocaleString()}` :
                        (request.Estimated_Amount ? `¥${request.Estimated_Amount.toLocaleString()}` : '-')}
                    </span>
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5
                  }}
                >
                  <PersonIcon sx={{ fontSize: '0.95rem' }} />
                  担当: {request.Member_Name}
                </Typography>

                {request.Updated_By_Name && (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.85rem',
                      mt: 0.5
                    }}
                  >
                    更新者: {request.Updated_By_Name}
                    {request.Updated_By_Name && ` (${formatFullDate(request.Updated_Date)})`}
                  </Typography>
                )}
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1.5,
                color: 'primary.main'
              }}>
                <ExpandMoreIcon
                  sx={{
                    transform: expandedRows.includes(index) ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s',
                    fontSize: '1.2rem'
                  }}
                />
              </Box>
            </Box>

            <Collapse in={expandedRows.includes(index)}>
              <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                {request.Remarks && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      申し送り事項:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        bgcolor: 'grey.50',
                        p: 1.5,
                        borderRadius: 1,
                        maxHeight: '100px',
                        overflow: 'auto'
                      }}
                    >
                      {request.Remarks}
                    </Typography>
                  </Box>
                )}

                {/* 計算書表示リンクを追加 */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<CalculateIcon />}
                    sx={{ width: '100%' }}
                    disabled={!request.Construction_ID}
                    onClick={() => handleNavigateToCalculationSheet(request.Construction_ID)}
                  >
                    計算書を表示
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEditDialog(request)}
                    sx={{ flex: 1 }}
                  >
                    編集
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleOpenDeleteDialog(request)}
                    sx={{ flex: 1 }}
                  >
                    削除
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  {!request.Is_Completed ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      fullWidth
                      onClick={() => handleUpdateStatusConfirm(request.ID, true)}
                      sx={{ py: 1, mt: 1 }}
                    >
                      作成完了
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      fullWidth
                      onClick={() => handleUpdateStatusConfirm(request.ID, false)}
                      sx={{ py: 1, mt: 1 }}
                    >
                      未作成に戻す
                    </Button>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Paper>
        ))
      )}
    </Box>
  );

  // デスクトップ表示
  const renderDesktopView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>状態</TableCell>
            <TableCell>工事番号</TableCell>
            <TableCell>客先名</TableCell>
            <TableCell>現場名</TableCell>
            <TableCell>工事内容</TableCell>
            <TableCell>請求月</TableCell>
            <TableCell>担当者</TableCell>
            <TableCell>見積金額</TableCell>
            <TableCell>請求金額</TableCell>
            <TableCell>更新者</TableCell>
            <TableCell align="center">計算書</TableCell>
            <TableCell align="center">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoiceRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} align="center">
                請求書作成依頼が見つかりません
              </TableCell>
            </TableRow>
          ) : (
            // 二行ずつ表示（メイン行と申し送り事項行）- 配列で返す方法に変更
            invoiceRequests.flatMap((request) => {
              const rows = [
                <TableRow key={`main-${request.ID}`} sx={{
                  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}>
                  <TableCell>
                    <Chip
                      label={request.Is_Completed ? '作成済み' : '未作成'}
                      color={request.Is_Completed ? 'success' : 'warning'}
                      size="small"
                      onClick={() => handleUpdateStatusConfirm(request.ID, !request.Is_Completed)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{request.Quotation_No || '-'}</TableCell>
                  <TableCell>{request.Customer_Name}</TableCell>
                  <TableCell>{request.Site_Name || '-'}</TableCell>
                  <TableCell>{request.Construction_Details}</TableCell>
                  <TableCell>{formatDate(request.Billing_Month)}</TableCell>
                  <TableCell>{request.Member_Name}</TableCell>
                  {/* 見積金額 */}
                  <TableCell>
                    {request.Estimated_Amount ?
                      `¥${request.Estimated_Amount.toLocaleString()}` :
                      '-'}
                  </TableCell>
                  {/* 請求金額 */}
                  <TableCell>
                    {request.Invoice_Amount ?
                      `¥${request.Invoice_Amount.toLocaleString()}` :
                      (request.Estimated_Amount ? `¥${request.Estimated_Amount.toLocaleString()}` : '-')}
                  </TableCell>
                  <TableCell>
                    {request.Updated_By_Name ? (
                      <>
                        {request.Updated_By_Name}
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          {formatFullDate(request.Updated_Date)}
                        </Typography>
                      </>
                    ) : '-'}
                  </TableCell>
                  {/* 計算書列 */}
                  <TableCell align="center">
                    {request.Construction_ID ? (
                      <Tooltip title="計算書を表示">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleNavigateToCalculationSheet(request.Construction_ID)}
                        >
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(request)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(request)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!request.Is_Completed ? (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleUpdateStatusConfirm(request.ID, true)}
                        >
                          作成完了
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => handleUpdateStatusConfirm(request.ID, false)}
                        >
                          未作成に戻す
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ];

              // 申し送り事項がある場合は行を追加
              if (request.Remarks) {
                rows.push(
                  <TableRow key={`remarks-${request.ID}`} sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    borderBottom: '2px solid rgba(224, 224, 224, 1)'
                  }}>
                    <TableCell sx={{ py: 1 }} align="right" size="small">
                      <Typography variant="caption" color="textSecondary">
                        申し送り事項:
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={11} sx={{ py: 1 }}>
                      <Box sx={{
                        p: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        maxHeight: '60px',
                        overflow: 'auto'
                      }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {request.Remarks}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              return rows;
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: { xs: 1, sm: 3 } }}>
        請求書作成依頼一覧
      </Typography>

      {/* 全担当者の未依頼工事一覧を追加 - 既存コンテンツの前に配置 */}
      <UnrequestedCompletedWorksList/>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="請求月"
              type="month"
              value={billingMonth}
              onChange={(e) => setBillingMonth(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& input': {
                  py: { xs: 1.5, md: 2 }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="担当者"
              fullWidth
              value={memberName}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleOpenMemberDialog}
                    >
                      <PersonSearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onClick={handleOpenMemberDialog}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              key={selectedCustomer ? 'customer-selected' : 'customer-empty'}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              options={customers}
              getOptionLabel={(option) => option.Customer_Name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="客先名"
                  variant="outlined"
                />
              )}
              noOptionsText="該当する顧客がいません"
              fullWidth
              clearOnBlur={false}
              clearOnEscape
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="工事番号"
              fullWidth
              value={quotationNo}
              onChange={(e) => setQuotationNo(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>作成状態</InputLabel>
              <Select
                value={isCompleted}
                onChange={(e) => setIsCompleted(e.target.value as string)}
                label="作成状態"
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="false">未作成</MenuItem>
                <MenuItem value="true">作成済み</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={clearSearchConditions}
              sx={{ height: '100%', minWidth: '120px' }}
            >
              条件クリア
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" align="center">
          {error}
        </Typography>
      ) : (
        isSmallScreen ? renderMobileView() : renderDesktopView()
      )}

      {/* 担当者選択ダイアログ */}
      <Dialog
        open={memberDialogOpen}
        onClose={handleCloseMemberDialog}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          px: 3,
          py: 2
        }}>
          担当者選択
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={1.5}>
            {filteredMembers
              .sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0))
              .map((member) => {
                const displayName = buildMemberShortName(member, members);
                const isSelected = memberName === displayName;

                return (
                  <Grid item xs={4} key={`member-${member.ID}`}>
                    <Button
                      fullWidth
                      variant={isSelected ? "contained" : "outlined"}
                      color={isSelected ? "success" : "primary"}
                      onClick={() => handleSelectMember(displayName)}
                      sx={{
                        height: '60px',
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                        textTransform: 'none',
                        ...(isSelected && {
                          fontWeight: 'bold',
                          boxShadow: 1,
                        }),
                      }}
                    >
                      <Box>
                        <Typography variant="body1">{displayName}</Typography>
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => handleSelectMember('')}
            variant="outlined"
            color="secondary"
          >
            選択解除
          </Button>
          <Button
            onClick={handleCloseMemberDialog}
            variant="outlined"
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 申し送り事項表示ダイアログ */}
      <Dialog
        open={remarksDialogOpen}
        onClose={() => setRemarksDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          申し送り事項
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {selectedRemarks}
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2
        }}>
          <Button
            onClick={() => setRemarksDialogOpen(false)}
            variant="outlined"
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* ステータス更新確認ダイアログ */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        disableRestoreFocus
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          確認
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography>
            {selectedRequest?.newStatus
              ? '請求書の作成を完了としてマークしますか？'
              : '請求書を未作成状態に戻しますか？'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2
        }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            variant="outlined"
          >
            キャンセル
          </Button>
          <Button
            onClick={handleUpdateStatus}
            color="primary"
            variant="contained"
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      {selectedInvoiceRequest && (
        <InvoiceRequestEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          invoiceRequest={selectedInvoiceRequest}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* 削除確認ダイアログ */}
      {selectedInvoiceRequest && (
        <InvoiceRequestDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          invoiceRequestId={selectedInvoiceRequest.ID}
          invoiceRequestInfo={{
            customerName: selectedInvoiceRequest.Customer_Name,
            siteName: selectedInvoiceRequest.Site_Name,
            billingMonth: selectedInvoiceRequest.Billing_Month
          }}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </Container>
  );
};

export default InvoiceRequestListPage;